import { AnimatePresence, motion } from 'framer-motion';
import {
  BarChart2,
  BookOpen,
  Briefcase,
  CheckCircle2,
  ClipboardList,
  FileText,
  Globe,
  History,
  Mail,
  PenLine,
  Search,
  Send,
  Sparkles,
  User,
  Wand2,
  Zap,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { useCareerWorkspacePreviewCopy } from './useCareerWorkspacePreviewCopy';
import type { CareerWorkspacePreviewCopy } from './careerWorkspacePreviewCopy';

type Phase =
  | 'intro'
  | 'typing'
  | 'research-action'
  | 'research-stream'
  | 'letter-thinking'
  | 'letter-stream'
  | 'record-offer'
  | 'complete';

type RightPanel = 'idle' | 'research' | 'letter';

const TYPING_MS = 26;

function usePreviewData(copy: CareerWorkspacePreviewCopy) {
  return useMemo(
    () => ({
      userMessage: copy.userMessage,
      chips: [
        { icon: User, label: copy.chipAboutProfile, desc: copy.chipAboutProfileDesc, color: 'bg-violet-50 text-violet-800 dark:bg-violet-950/30 dark:text-violet-300' },
        { icon: Sparkles, label: copy.chipRecommendedJobs, desc: copy.chipRecommendedJobsDesc, color: 'bg-indigo-50 text-indigo-800 dark:bg-indigo-950/30 dark:text-indigo-300' },
        { icon: BarChart2, label: copy.chipSkillAssessment, desc: copy.chipSkillAssessmentDesc, color: 'bg-sky-50 text-sky-800 dark:bg-sky-950/30 dark:text-sky-300' },
        { icon: FileText, label: copy.chipOptimizeResume, desc: copy.chipOptimizeResumeDesc, color: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300' },
        { icon: PenLine, label: copy.chipCoverLetter, desc: copy.chipCoverLetterDesc, color: 'bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300' },
        { icon: Mail, label: copy.chipApplicationLetter, desc: copy.chipApplicationLetterDesc, color: 'bg-rose-50 text-rose-800 dark:bg-rose-950/30 dark:text-rose-300' },
      ],
      sessions: [
        { title: copy.sessionStripePrep, active: true, time: copy.sessionNow },
        { title: copy.sessionMicrosoftOptimize, active: false, time: copy.session2hAgo },
        { title: copy.sessionProfileAssessment, active: false, time: copy.sessionYesterday },
      ],
      researchSections: [
        { title: copy.researchOverview, body: copy.researchOverviewBody },
        { title: copy.researchRecentNews, body: copy.researchRecentNewsBody },
        { title: copy.researchProduct, body: copy.researchProductBody },
        { title: copy.researchCulture, body: copy.researchCultureBody },
        { title: copy.researchInterviewTopics, body: copy.researchInterviewTopicsBody },
      ],
      profileContext: [copy.profileTagName, copy.profileTagSeniority, copy.profileTagSkills, copy.profileTagLocation],
      artifacts: [
        { icon: Globe, label: copy.artifactCompanyResearch, kind: copy.artifactKindCompanyResearch, time: copy.artifactJustNow, color: 'text-violet-500', fresh: true },
        { icon: PenLine, label: copy.artifactCoverLetter, kind: copy.artifactKindCoverLetter, time: copy.artifactJustNow, color: 'text-indigo-500', fresh: true },
        { icon: FileText, label: copy.artifactOptimization, kind: copy.artifactKindOptimization, time: copy.artifact2hAgo, color: 'text-sky-500', fresh: false },
        { icon: Mail, label: copy.artifactApplicationLetter, kind: copy.artifactKindApplicationLetter, time: copy.artifactYesterday, color: 'text-rose-500', fresh: false },
        { icon: User, label: copy.artifactProfileAssessment, kind: copy.artifactKindProfileAssessment, time: copy.artifact2dAgo, color: 'text-emerald-500', fresh: false },
      ],
    }),
    [copy],
  );
}

const COVER_LETTER_LINES = [
  'Dear Hiring Manager,',
  '',
  'With 6+ years shipping B2B SaaS products trusted by 40K+ enterprise users,',
  "I'm excited to bring roadmap and stakeholder craft to Stripe's Senior PM team.",
  '',
  'I drove 3× revenue growth leading a 12-person cross-functional team — from',
  "discovery through launch — a pattern that maps to Stripe's platform mission.",
  '',
  'Thank you for your consideration. I would welcome the opportunity to discuss',
  'how my experience aligns with your Bangalore platform PM charter.',
];

function AgentBubble({
  children,
  tone = 'neutral',
}: {
  children: React.ReactNode;
  tone?: 'neutral' | 'action' | 'success';
}) {
  const styles =
    tone === 'action'
      ? 'border-violet-100/70 bg-violet-50/50 dark:border-violet-900/40 dark:bg-violet-950/20'
      : tone === 'success'
        ? 'border-emerald-100/70 bg-emerald-50/50 dark:border-emerald-900/40 dark:bg-emerald-950/20'
        : 'border-indigo-100/60 bg-indigo-50/40 dark:border-indigo-900/40 dark:bg-indigo-950/20';

  return (
    <div className={`rounded-xl border p-2 ${styles}`}>
      <div className="text-[8.5px] leading-relaxed text-gray-700 dark:text-gray-300">{children}</div>
    </div>
  );
}

function ActionTag({ label }: { label: string }) {
  return (
    <code className="mt-1 block rounded bg-gray-900/90 px-1.5 py-0.5 font-mono text-[7px] text-emerald-300">
      {label}
    </code>
  );
}

function ResearchPanel({
  visibleSections,
  sections,
  copy,
}: {
  visibleSections: number;
  sections: { title: string; body: string }[];
  copy: CareerWorkspacePreviewCopy;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col bg-[#fafafa] dark:bg-[#111113]">
      <div className="flex shrink-0 items-center gap-2 border-b border-gray-200 bg-white px-3 py-2 dark:border-gray-800 dark:bg-[#0f0f11]">
        <Globe className="h-3 w-3 shrink-0 text-violet-600" />
        <span className="min-w-0 flex-1 truncate text-[10px] font-semibold text-gray-900 dark:text-white">
          {copy.artifactCompanyResearch}
        </span>
        <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[7px] font-semibold text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
          {copy.exaLive}
        </span>
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-hidden p-3">
        {sections.slice(0, visibleSections).map((sec, i) => (
          <motion.div
            key={sec.title}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className="rounded-lg border border-gray-100 bg-white p-2 dark:border-gray-800 dark:bg-[#0f0f11]"
          >
            <p className="mb-0.5 text-[8px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
              {sec.title}
            </p>
            <p className="text-[8px] leading-relaxed text-gray-600 dark:text-gray-400">{sec.body}</p>
          </motion.div>
        ))}
        {visibleSections < sections.length && (
          <div className="flex items-center gap-1.5 text-[8px] text-gray-400">
            <Search className="h-2.5 w-2.5 animate-pulse" />
            Synthesising from live web sources…
          </div>
        )}
      </div>
    </div>
  );
}

function LetterCanvas({
  streamedLines,
  streamedChars,
  streaming,
}: {
  streamedLines: number;
  streamedChars: number;
  streaming: boolean;
}) {
  const tabs = [
    { id: 'resume', label: 'Resume', icon: FileText },
    { id: 'cover', label: 'Cover Letter', icon: PenLine, active: true },
    { id: 'note', label: 'Application Note', icon: Send },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#f8fafc] dark:bg-[#111113]">
      <div className="flex shrink-0 items-center gap-2 border-b border-gray-200 bg-white px-3 py-2 dark:border-gray-800 dark:bg-[#0f0f11]">
        <Wand2 className="h-3 w-3 shrink-0 text-indigo-600" />
        <span className="min-w-0 flex-1 truncate text-[10px] font-semibold text-gray-900 dark:text-white">
          Cover letter · Senior PM — Stripe
        </span>
      </div>
      <div className="flex shrink-0 gap-0.5 border-b border-gray-200 bg-white px-2 py-1 dark:border-gray-800 dark:bg-[#0f0f11]">
        {tabs.map((tab) => (
          <span
            key={tab.id}
            className={`flex items-center gap-1 rounded-md px-2 py-1 text-[7.5px] font-medium ${
              tab.active
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300'
                : 'text-gray-400'
            }`}
          >
            <tab.icon className="h-2.5 w-2.5" />
            {tab.label}
          </span>
        ))}
      </div>
      <div className="min-h-0 flex-1 overflow-hidden p-2">
        <div className="relative mx-auto h-full overflow-hidden rounded-sm bg-white p-3 shadow-md dark:bg-white">
          <div className="space-y-0.5 text-[8px] leading-relaxed text-gray-800">
            {COVER_LETTER_LINES.slice(0, streamedLines - 1).map((line, i) => (
              <p key={i} className={line === '' ? 'h-1.5' : ''}>
                {line}
              </p>
            ))}
            {streamedLines > 0 && streamedLines <= COVER_LETTER_LINES.length && (
              <p>
                {COVER_LETTER_LINES[streamedLines - 1].slice(0, streamedChars)}
                {streaming && (
                  <span className="inline-block h-3 w-[1.5px] animate-pulse bg-indigo-500 align-middle" />
                )}
              </p>
            )}
          </div>
        </div>
      </div>
      {!streaming && streamedLines >= COVER_LETTER_LINES.length && (
        <div className="flex shrink-0 gap-1.5 border-t border-gray-200 bg-white px-3 py-2 dark:border-gray-800 dark:bg-[#0f0f11]">
          <button type="button" className="hfs-agent-preview__btn hfs-agent-preview__btn--secondary flex-1 text-[8px]">
            Copy
          </button>
          <button type="button" className="hfs-agent-preview__btn hfs-agent-preview__btn--primary flex-1 text-[8px]">
            <Zap className="h-3 w-3" />
            Apply
          </button>
        </div>
      )}
    </div>
  );
}

const AgenticCareerWorkspacePreview: React.FC = () => {
  const copy = useCareerWorkspacePreviewCopy();
  const preview = usePreviewData(copy);
  const USER_MESSAGE = preview.userMessage;
  const [phase, setPhase] = useState<Phase>('intro');
  const [typedLen, setTypedLen] = useState(0);
  const [rightPanel, setRightPanel] = useState<RightPanel>('idle');
  const [researchSections, setResearchSections] = useState(0);
  const [streamedLines, setStreamedLines] = useState(0);
  const [streamedChars, setStreamedChars] = useState(0);
  const [savedArtifacts, setSavedArtifacts] = useState(0);
  const [showApplication, setShowApplication] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timers: number[] = [];
    const sleep = (ms: number) =>
      new Promise<void>((res) => {
        timers.push(window.setTimeout(res, ms));
      });

    const reset = () => {
      setPhase('intro');
      setTypedLen(0);
      setRightPanel('idle');
      setResearchSections(0);
      setStreamedLines(0);
      setStreamedChars(0);
      setSavedArtifacts(0);
      setShowApplication(false);
    };

    const streamLetter = async () => {
      for (let line = 0; line < COVER_LETTER_LINES.length; line++) {
        if (cancelled) return;
        setStreamedLines(line + 1);
        const lineText = COVER_LETTER_LINES[line];
        for (let ch = 0; ch <= lineText.length; ch++) {
          if (cancelled) return;
          setStreamedChars(ch);
          await sleep(lineText.length > 0 ? 16 : 45);
        }
        await sleep(lineText.length === 0 ? 40 : 25);
      }
    };

    const run = async () => {
      await sleep(400);
      while (!cancelled) {
        reset();
        await sleep(1600);

        setPhase('typing');
        for (let i = 0; i <= USER_MESSAGE.length; i++) {
          if (cancelled) return;
          setTypedLen(i);
          await sleep(TYPING_MS);
        }
        await sleep(450);

        setPhase('research-action');
        await sleep(900);

        setPhase('research-stream');
        setRightPanel('research');
        for (let s = 1; s <= preview.researchSections.length; s++) {
          if (cancelled) return;
          setResearchSections(s);
          await sleep(480);
        }
        setSavedArtifacts(1);
        await sleep(700);

        setPhase('letter-thinking');
        await sleep(1100);

        setPhase('letter-stream');
        setRightPanel('letter');
        setStreamedLines(0);
        setStreamedChars(0);
        await streamLetter();
        setSavedArtifacts(2);
        await sleep(600);

        setPhase('record-offer');
        await sleep(2200);

        setPhase('complete');
        setShowApplication(true);
        await sleep(5000);
      }
    };

    void run();
    return () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [USER_MESSAGE.length, preview.researchSections.length]);

  const showTyping = phase === 'typing' && typedLen < USER_MESSAGE.length;
  const pastIntro = phase !== 'intro';
  const showResearchChat = phase === 'research-action' || phase === 'research-stream' || pastIntro && phase !== 'typing';
  const showLetterChat = ['letter-thinking', 'letter-stream', 'record-offer', 'complete'].includes(phase);
  const showRecordChat = ['record-offer', 'complete'].includes(phase);

  return (
    <div className="relative flex h-full w-full min-h-0 flex-col overflow-hidden bg-white font-sans antialiased dark:bg-[#0f0f11]">
      <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-3 py-2 dark:border-gray-800">
        <Sparkles className="h-3.5 w-3.5 shrink-0 text-indigo-600" />
        <span className="text-[11px] font-semibold text-gray-900 dark:text-white">{copy.title}</span>
        <span className="ml-auto flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 dark:bg-indigo-950/40">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
          <span className="text-[8px] font-medium text-indigo-700 dark:text-indigo-300">{copy.actionsBadge}</span>
        </span>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-12 overflow-hidden">
        {/* History sidebar */}
        <div className="col-span-2 flex min-h-0 flex-col border-r border-gray-100 bg-gray-50/80 dark:border-gray-800 dark:bg-[#0a0a0c]">
          <div className="flex shrink-0 items-center gap-1 border-b border-gray-100 px-2 py-1.5 dark:border-gray-800">
            <History className="h-3 w-3 text-gray-400" />
            <span className="text-[8px] font-bold uppercase tracking-wider text-gray-400">{copy.sessions}</span>
          </div>
          <div className="min-h-0 flex-1 space-y-0.5 overflow-hidden p-1.5">
            {preview.sessions.map((s) => (
              <div
                key={s.title}
                className={`rounded-lg px-2 py-1.5 ${
                  s.active
                    ? 'border border-indigo-200 bg-white dark:border-indigo-800/60 dark:bg-indigo-950/20'
                    : 'border border-transparent'
                }`}
              >
                <p className="truncate text-[8px] font-semibold text-gray-800 dark:text-gray-200">{s.title}</p>
                <p className="text-[7px] text-gray-400">{s.time}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Agent chat */}
        <div className="col-span-5 flex min-h-0 flex-col border-r border-gray-100 dark:border-gray-800">
          <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-2.5 py-1.5 dark:border-gray-800">
            <Sparkles className="h-3 w-3 text-indigo-600" />
            <span className="text-[10px] font-semibold text-gray-900 dark:text-white">{copy.agentBrand}</span>
          </div>

          <div className="min-h-0 flex-1 space-y-2 overflow-hidden p-2.5">
            {/* Profile context injected */}
            <div className="rounded-lg border border-dashed border-indigo-200 bg-indigo-50/40 px-2 py-1.5 dark:border-indigo-900/40 dark:bg-indigo-950/15">
              <p className="text-[7px] font-bold uppercase tracking-wider text-indigo-600">{copy.profileContextInjected}</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {preview.profileContext.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-white/80 px-1.5 py-0.5 text-[6.5px] font-medium text-gray-600 dark:bg-gray-900/60 dark:text-gray-300"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Capability chips */}
            <AnimatePresence>
              {phase === 'intro' && (
                <motion.div key="chips" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-1">
                  <p className="text-[7px] font-bold uppercase tracking-wider text-gray-400">{copy.quickActions}</p>
                  <div className="grid grid-cols-2 gap-1">
                    {preview.chips.map((chip) => (
                      <div
                        key={chip.label}
                        className={`flex flex-col gap-0.5 rounded-lg px-2 py-1.5 ${chip.color}`}
                      >
                        <div className="flex items-center gap-1">
                          <chip.icon className="h-2.5 w-2.5 shrink-0" />
                          <span className="truncate text-[7.5px] font-semibold">{chip.label}</span>
                        </div>
                        <span className="truncate text-[6.5px] opacity-70">{chip.desc}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* User message */}
            {pastIntro && (
              <div>
                <span className="text-[7px] font-bold uppercase tracking-wider text-gray-400">{copy.you}</span>
                <div className="mt-0.5 rounded-xl border border-gray-100 bg-gray-50 px-2 py-1.5 text-[8.5px] leading-relaxed text-gray-900 dark:border-gray-800 dark:bg-gray-900/60 dark:text-gray-100">
                  {USER_MESSAGE.slice(0, typedLen)}
                  {showTyping && (
                    <span className="ml-0.5 inline-block h-3 w-[1.5px] animate-pulse bg-gray-800 align-middle dark:bg-white" />
                  )}
                </div>
              </div>
            )}

            {/* Research action */}
            {showResearchChat && phase !== 'typing' && (
              <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
                <span className="text-[7px] font-bold uppercase tracking-wider text-indigo-600">{copy.agentRole}</span>
                <div className="mt-0.5">
                  <AgentBubble tone="action">
                    {copy.researchAction}
                    <ActionTag label='{"action":"company_research","company":"Stripe"}' />
                  </AgentBubble>
                </div>
              </motion.div>
            )}

            {/* Letter thinking */}
            {showLetterChat && (
              <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
                <span className="text-[7px] font-bold uppercase tracking-wider text-indigo-600">{copy.agentRole}</span>
                <div className="mt-0.5">
                  <AgentBubble>
                    {phase === 'letter-thinking' ? (
                      <span className="flex items-center gap-1.5">
                        <Sparkles className="h-2.5 w-2.5 animate-pulse text-indigo-500" />
                        {copy.letterThinking}
                      </span>
                    ) : (
                      <>
                        {copy.letterReady}
                        <ActionTag label='{"action":"cover_letter","jobTitle":"Senior PM","company":"Stripe"}' />
                      </>
                    )}
                  </AgentBubble>
                </div>
              </motion.div>
            )}

            {/* Artifact saved badges */}
            {savedArtifacts >= 1 && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex items-center gap-1.5 rounded-lg border border-emerald-100 bg-emerald-50/60 px-2 py-1 dark:border-emerald-900/30 dark:bg-emerald-950/20">
                <BookOpen className="h-2.5 w-2.5 text-emerald-600" />
                <span className="text-[7.5px] font-semibold text-emerald-700 dark:text-emerald-300">
                  Research saved to Artifacts
                </span>
              </motion.div>
            )}
            {savedArtifacts >= 2 && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex items-center gap-1.5 rounded-lg border border-emerald-100 bg-emerald-50/60 px-2 py-1 dark:border-emerald-900/30 dark:bg-emerald-950/20">
                <PenLine className="h-2.5 w-2.5 text-emerald-600" />
                <span className="text-[7.5px] font-semibold text-emerald-700 dark:text-emerald-300">
                  Cover letter saved to Artifacts
                </span>
              </motion.div>
            )}

            {/* Record application offer */}
            {showRecordChat && (
              <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
                <span className="text-[7px] font-bold uppercase tracking-wider text-indigo-600">{copy.agentRole}</span>
                <div className="mt-0.5">
                  <AgentBubble tone="success">
                    {copy.recordOffer}
                    <ActionTag label='{"action":"record_application","jobTitle":"Senior PM","company":"Stripe"}' />
                  </AgentBubble>
                </div>
              </motion.div>
            )}

            {showApplication && (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 rounded-lg border border-sky-100 bg-sky-50/60 px-2 py-1.5 dark:border-sky-900/30 dark:bg-sky-950/20"
              >
                <ClipboardList className="h-3 w-3 shrink-0 text-sky-600" />
                <div>
                  <p className="text-[8px] font-bold text-sky-800 dark:text-sky-300">Application recorded</p>
                  <p className="text-[7px] text-gray-500">Senior PM · Stripe · source: agent</p>
                </div>
                <CheckCircle2 className="ml-auto h-3 w-3 text-emerald-500" />
              </motion.div>
            )}
          </div>

          <div className="shrink-0 border-t border-dashed border-gray-200 px-2 py-1.5 dark:border-gray-700">
            <div className="flex items-center gap-1.5 rounded-lg border border-gray-100 bg-gray-50 px-2 py-1 dark:border-gray-800 dark:bg-gray-900/40">
              <span className="flex-1 text-[7.5px] text-gray-400">{copy.promptHint}</span>
              <Send className="h-3 w-3 text-indigo-500" />
            </div>
          </div>
        </div>

        {/* Right: canvas + artifacts */}
        <div className="col-span-5 flex min-h-0 flex-col">
          <div className="min-h-0 flex-1 overflow-hidden">
            <AnimatePresence mode="wait">
              {rightPanel === 'research' && (
                <motion.div key="research" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="h-full">
                  <ResearchPanel visibleSections={researchSections} sections={preview.researchSections} copy={copy} />
                </motion.div>
              )}
              {rightPanel === 'letter' && (
                <motion.div key="letter" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="h-full">
                  <LetterCanvas
                    streamedLines={streamedLines}
                    streamedChars={streamedChars}
                    streaming={phase === 'letter-stream'}
                  />
                </motion.div>
              )}
              {rightPanel === 'idle' && (
                <motion.div
                  key="idle"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex h-full flex-col items-center justify-center gap-2 bg-[#fafafa] p-4 dark:bg-[#111113]"
                >
                  <Briefcase className="h-8 w-8 text-indigo-200 dark:text-indigo-900" />
                  <p className="text-center text-[9px] font-medium text-gray-500">
                    Canvas opens for research, letters &amp; optimized resumes
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex max-h-[38%] min-h-0 shrink-0 flex-col border-t border-gray-100 dark:border-gray-800">
            <div className="flex shrink-0 items-center justify-between px-2.5 py-1.5">
              <span className="text-[8px] font-bold uppercase tracking-wider text-gray-400">{copy.savedArtifacts}</span>
              <span className="text-[7px] text-gray-400">{copy.savedArtifactsCount}</span>
            </div>
            <div className="min-h-0 flex-1 space-y-0.5 overflow-hidden px-2 pb-2">
              {preview.artifacts.map((art, i) => {
                const isFresh = art.fresh && (
                  (art.kind === 'company_research' && savedArtifacts >= 1) ||
                  (art.kind === 'cover_letter' && savedArtifacts >= 2)
                );
                return (
                  <motion.div
                    key={art.label}
                    initial={{ opacity: 0, x: 4 }}
                    animate={{ opacity: isFresh || !art.fresh ? 1 : 0.35, x: 0 }}
                    className={`flex items-center gap-2 rounded-lg px-2 py-1 ${
                      isFresh
                        ? 'border border-emerald-100 bg-emerald-50/50 dark:border-emerald-900/30 dark:bg-emerald-950/10'
                        : 'border border-transparent'
                    }`}
                  >
                    <art.icon className={`h-3 w-3 shrink-0 ${art.color}`} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[7.5px] font-medium text-gray-700 dark:text-gray-300">{art.label}</p>
                      <p className="text-[6.5px] uppercase tracking-wide text-gray-400">{art.kind.replace('_', ' ')}</p>
                    </div>
                    <span className="shrink-0 text-[7px] text-gray-400">{isFresh ? copy.sessionNow : art.time}</span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgenticCareerWorkspacePreview;
